import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { motion } from 'framer-motion';
import { FaTicketAlt, FaCalendar, FaClock, FaLink, FaPlay } from 'react-icons/fa';
import { BsInfoCircle } from 'react-icons/bs';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CircularProgress, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Grid
} from '@mui/material';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  color: white;
  margin-bottom: 1rem;
  font-size: 2rem;

  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background: #4a90e2;
    margin: 1rem auto;
    border-radius: 2px;
  }
`;

const TicketGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: white;
  margin: 2rem 0 1rem;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TicketCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(74, 144, 226, 0.1);
  position: relative;
`;

const LiveBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: #e74c3c;
  color: white;
  padding: 0.5rem 1rem;
  font-weight: bold;
  font-size: 0.8rem;
  z-index: 10;
  border-bottom-left-radius: 10px;
`;

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
`;

const EventType = styled.span`
  background: #4a90e2;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: bold;
`;

const TicketStatus = styled.span`
  background: ${props => 
    props.active ? '#2ecc71' : 
    props.pending ? '#f39c12' : 
    props.failed ? '#e74c3c' : '#666'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: bold;
`;

const EventImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
  filter: ${props => props.past ? 'grayscale(80%)' : 'none'};
`;

const TicketContent = styled.div`
  padding: 1.5rem;
`;

const EventTitle = styled.h3`
  color: white;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #ccc;
  font-size: 0.9rem;
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  background: ${props => 
    props.pending ? 'rgba(243, 156, 18, 0.1)' : 
    props.failed ? 'rgba(231, 76, 60, 0.1)' : 'rgba(46, 204, 113, 0.1)'};
  color: ${props => 
    props.pending ? '#f39c12' : 
    props.failed ? '#e74c3c' : '#2ecc71'};
`;

const TicketActions = styled.div`
  margin-top: 1rem;
`;

const NoTicketsMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #888;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
`;

const NoTicketsIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #4a90e2;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const ErrorContainer = styled(motion.div)`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #e74c3c;
  padding: 2rem;
  text-align: center;
  border-radius: 15px;
  margin: 2rem 0;
`;

const MyTickets = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showAccessDialog, setShowAccessDialog] = useState(false);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!currentUser) {
      localStorage.setItem('konzo_redirect_after_login', '/streaming/my-tickets');
      navigate('/marketplace/login');
      return;
    }
    
    // Check for pending tickets in session storage (recovery from payment flow)
    const checkPendingTickets = () => {
      try {
        const pendingTicketData = sessionStorage.getItem('pendingTicket');
        if (pendingTicketData) {
          const pendingTicket = JSON.parse(pendingTicketData);
          console.log('Found pending ticket in session storage:', pendingTicket);
          
          // Only process if it belongs to the current user
          if (pendingTicket.userId === currentUser.uid || pendingTicket.userId === 'guest') {
            // We'll handle this after fetching tickets from Firestore
            return pendingTicket;
          }
        }
      } catch (error) {
        console.error('Error checking pending tickets:', error);
      }
      return null;
    };
    
    const fetchTickets = async () => {
      try {
        setLoading(true);
        
        // Get pending ticket from session storage if any
        const pendingTicket = checkPendingTickets();
        
        // Query tickets from Firestore
        const ticketsQuery = query(
          collection(db, 'tickets'),
          where('userId', '==', currentUser.uid)
        );
        
        const ticketsSnapshot = await getDocs(ticketsQuery);
        let firestoreTickets = [];
        
        if (!ticketsSnapshot.empty) {
          firestoreTickets = ticketsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'firestore'
          }));
        }
        
        // Combine with pending ticket if it exists and not already in Firestore
        let allTickets = [...firestoreTickets];
        
        if (pendingTicket) {
          // Check if this pending ticket is already in Firestore
          const ticketExists = firestoreTickets.some(t => 
            (t.fallbackId && t.fallbackId === pendingTicket.fallbackId) || 
            (pendingTicket.id && t.id === pendingTicket.id)
          );
          
          if (!ticketExists) {
            // Add the pending ticket to the list
            allTickets.push({
              ...pendingTicket,
              source: 'sessionStorage',
              status: 'pending' // Force pending status for session storage tickets
            });
          } else {
            // Clear from session storage since it's already in Firestore
            sessionStorage.removeItem('pendingTicket');
          }
        }
        
        // Get event details for each ticket
        const ticketsWithEvents = await Promise.all(
          allTickets.map(async (ticket) => {
            try {
              // Get event details
              const eventDoc = await getDoc(doc(db, 'streamEvents', ticket.eventId));
              
              if (!eventDoc.exists()) {
                return {
                  ...ticket,
                  event: {
                    id: ticket.eventId,
                    title: ticket.eventTitle || 'Event no longer available',
                    date: new Date().toISOString(),
                    time: '00:00',
                    image: 'https://via.placeholder.com/300x150?text=Event+Unavailable'
                  },
                  error: 'Event details not found'
                };
              }
              
              // Determine ticket status
              let status = ticket.status || 'pending';
              let statusMessage = '';
              
              // For tickets in 'processing' status, check if they're actually completed
              if (status === 'processing' && ticket.transactionId) {
                // In a real implementation, you would check the payment status here
                // For now, we'll just use the existing status
                statusMessage = 'Payment is being processed';
              }
              
              // Calculate if the event is live, upcoming, or past
              const eventDate = new Date(eventDoc.data().date + 'T' + eventDoc.data().time);
              const now = new Date();
              let eventStatus = 'upcoming';
              
              if (now > eventDate) {
                // Check if it's still within the duration window
                const duration = eventDoc.data().duration || 2; // Default 2 hours
                const endTime = new Date(eventDate.getTime() + (duration * 60 * 60 * 1000));
                
                if (now > endTime) {
                  eventStatus = 'past';
                } else {
                  eventStatus = 'live';
                }
              }
              
              return {
                ...ticket,
                status,
                statusMessage,
                eventStatus,
                event: {
                  id: eventDoc.id,
                  ...eventDoc.data()
                }
              };
            } catch (eventError) {
              console.error('Error fetching event details:', eventError);
              return {
                ...ticket,
                event: {
                  id: ticket.eventId,
                  title: ticket.eventTitle || 'Error loading event',
                  date: new Date().toISOString(),
                  time: '00:00',
                  image: 'https://via.placeholder.com/300x150?text=Error+Loading+Event'
                },
                error: 'Failed to load event details'
              };
            }
          })
        );

        // Sort tickets: live events first, then upcoming, then past
        const sortedTickets = ticketsWithEvents.sort((a, b) => {
          // First by event status
          const statusOrder = { 'live': 0, 'upcoming': 1, 'past': 2 };
          if (statusOrder[a.eventStatus] !== statusOrder[b.eventStatus]) {
            return statusOrder[a.eventStatus] - statusOrder[b.eventStatus];
          }
          
          // Then by date (newest first for upcoming, oldest first for past)
          const dateA = new Date(a.event.date + 'T' + a.event.time);
          const dateB = new Date(b.event.date + 'T' + b.event.time);
          
          if (a.eventStatus === 'upcoming') {
            return dateA - dateB; // Soonest first
          } else {
            return dateB - dateA; // Most recent first
          }
        });

        setTickets(sortedTickets);
      } catch (error) {
        console.error('Error fetching tickets:', error);
        setError('Failed to load your tickets. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [currentUser, navigate]);

  // Check if an event is live (happening now)
  const isEventLive = (event) => {
    try {
      if (!event || !event.date || !event.time) return false;
      
      const eventDate = new Date(event.date + 'T' + event.time);
      const now = new Date();
      
      // Event has started
      if (now >= eventDate) {
        // Check if it's still ongoing based on duration
        const duration = event.duration || 2; // Default 2 hours if not specified
        const endTime = new Date(eventDate.getTime() + (duration * 60 * 60 * 1000));
        return now <= endTime;
      }
      return false;
    } catch (error) {
      console.error('Error checking event status:', error);
      return false;
    }
  };
  
  // Check if an event is upcoming (in the future)
  const isEventUpcoming = (event) => {
    try {
      if (!event || !event.date || !event.time) return false;
      
      const eventDate = new Date(event.date + 'T' + event.time);
      const now = new Date();
      
      return eventDate > now;
    } catch (error) {
      console.error('Error checking event status:', error);
      return false;
    }
  };
  
  // Check if an event is past (already ended)
  const isEventPast = (event) => {
    try {
      if (!event || !event.date || !event.time) return false;
      
      const eventDate = new Date(event.date + 'T' + event.time);
      const duration = event.duration || 2; // Default 2 hours if not specified
      const endTime = new Date(eventDate.getTime() + (duration * 60 * 60 * 1000));
      const now = new Date();
      
      return now > endTime;
    } catch (error) {
      console.error('Error checking event status:', error);
      return false;
    }
  };
  
  // Handle accessing the streaming event
  const handleAccessEvent = (ticket) => {
    // Check if payment is completed
    if (ticket.status !== 'active' && ticket.status !== 'completed') {
      // If payment is still pending, show appropriate message
      if (ticket.status === 'pending' || ticket.status === 'processing') {
        alert('Your payment is still being processed. Please try again in a few minutes.');
        return;
      }
      
      // If payment failed, offer to retry
      if (ticket.status === 'failed') {
        if (window.confirm('Your payment was not successful. Would you like to try again?')) {
          navigate(`/streaming/${ticket.eventId}`);
        }
        return;
      }
    }
    
    // Check if event is live or upcoming
    if (isEventPast(ticket.event)) {
      alert('This event has already ended.');
      return;
    }
    
    if (isEventUpcoming(ticket.event)) {
      const eventDate = new Date(ticket.event.date + 'T' + ticket.event.time);
      const now = new Date();
      const timeUntilEvent = Math.ceil((eventDate - now) / (1000 * 60)); // minutes until event
      
      if (timeUntilEvent > 30) {
        // More than 30 minutes until event starts
        alert(`This event hasn't started yet. It will begin on ${eventDate.toLocaleDateString()} at ${ticket.event.time}.`);
        return;
      }
      
      // Within 30 minutes of event, allow early access to waiting room
      alert(`The event will start in approximately ${timeUntilEvent} minutes. You're being directed to the waiting room.`);
    }
    
    // Set the selected ticket and show access dialog
    setSelectedTicket(ticket);
    setShowAccessDialog(true);
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>My Tickets</Title>
        </Header>
        <LoadingContainer>
          <CircularProgress style={{ color: '#4a90e2' }} />
        </LoadingContainer>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container>
        <Header>
          <Title>My Tickets</Title>
        </Header>
        <ErrorContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {error}
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>My Tickets</Title>
        <p>View and access your purchased streaming events</p>
      </Header>
      
      {tickets.length === 0 ? (
        <NoTicketsMessage>
          <NoTicketsIcon>
            <FaTicketAlt />
          </NoTicketsIcon>
          <h3>You don't have any tickets yet</h3>
          <p>Browse upcoming events and purchase tickets to get started.</p>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/streaming')}
            sx={{ mt: 2 }}
          >
            Browse Events
          </Button>
        </NoTicketsMessage>
      ) : (
        <>
          {tickets.filter(ticket => ticket.eventStatus === 'live').length > 0 && (
            <>
              <SectionTitle>
                <FaPlay style={{ color: '#e74c3c' }} /> Live Now
              </SectionTitle>
              <TicketGrid>
                {tickets
                  .filter(ticket => ticket.eventStatus === 'live')
                  .map(ticket => renderTicketCard(ticket, true))
                }
              </TicketGrid>
            </>
          )}
          
          {tickets.filter(ticket => ticket.eventStatus === 'upcoming').length > 0 && (
            <>
              <SectionTitle>
                <FaCalendar /> Upcoming Events
              </SectionTitle>
              <TicketGrid>
                {tickets
                  .filter(ticket => ticket.eventStatus === 'upcoming')
                  .map(ticket => renderTicketCard(ticket))
                }
              </TicketGrid>
            </>
          )}
          
          {tickets.filter(ticket => ticket.eventStatus === 'past').length > 0 && (
            <>
              <SectionTitle>
                <FaClock /> Past Events
              </SectionTitle>
              <TicketGrid>
                {tickets
                  .filter(ticket => ticket.eventStatus === 'past')
                  .map(ticket => renderTicketCard(ticket))
                }
              </TicketGrid>
            </>
          )}
        </>
      )}
      
      {/* Dialog for accessing stream */}
      <Dialog 
        open={showAccessDialog} 
        onClose={() => setShowAccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', bgcolor: '#1a1a2e', color: 'white' }}>
          {selectedTicket?.eventStatus === 'live' ? 'Join Live Stream' : 'Event Access'}
        </DialogTitle>
        <DialogContent sx={{ p: 4, mt: 2 }}>
          {selectedTicket && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: '#4a90e2', marginBottom: '1rem' }}>{selectedTicket.event.title}</h2>
              
              {selectedTicket.eventStatus === 'live' ? (
                <>
                  <p style={{ marginBottom: '1.5rem' }}>This event is live now! Click below to join the stream.</p>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    startIcon={<FaPlay />}
                    onClick={() => {
                      window.open(selectedTicket.event.streamingUrl || 'https://meet.google.com', '_blank');
                      setShowAccessDialog(false);
                    }}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    Join Live Stream
                  </Button>
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0 }}>Important Information</h4>
                    <p style={{ fontSize: '0.9rem', color: '#666' }}>
                      Please ensure you have a stable internet connection. If you experience any issues, try refreshing your browser.
                    </p>
                  </div>
                </>
              ) : selectedTicket.eventStatus === 'upcoming' ? (
                <>
                  <p>This event will begin on {new Date(selectedTicket.event.date + 'T' + selectedTicket.event.time).toLocaleString()}.</p>
                  <p style={{ marginTop: '1rem' }}>You'll be able to join the stream 15 minutes before the start time.</p>
                  
                  <div style={{ margin: '1.5rem 0', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ margin: 0 }}>Event Details</h4>
                    <p style={{ margin: '0.5rem 0 0', color: '#666' }}>
                      {selectedTicket.event.description || 'No additional details available.'}
                    </p>
                  </div>
                  
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      // Add to calendar functionality could be added here
                      alert('Calendar reminder feature coming soon!');
                    }}
                    sx={{ mt: 1 }}
                  >
                    Add to Calendar
                  </Button>
                </>
              ) : (
                <>
                  <p>This event has ended. {selectedTicket.event.recordingUrl ? 'You can watch the recording below.' : 'Recording not available.'}</p>
                  
                  {selectedTicket.event.recordingUrl && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      startIcon={<FaPlay />}
                      onClick={() => {
                        window.open(selectedTicket.event.recordingUrl, '_blank');
                        setShowAccessDialog(false);
                      }}
                      sx={{ my: 2 }}
                    >
                      Watch Recording
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setShowAccessDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
  
  // Helper function to render ticket cards
  function renderTicketCard(ticket, isLive = false) {
    const isPending = ticket.status === 'pending' || ticket.status === 'processing';
    const isFailed = ticket.status === 'failed';
    const isPast = ticket.eventStatus === 'past';
    
    return (
      <TicketCard
        key={ticket.id || `temp-${ticket.fallbackId}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
      >
        {isLive && (
          <LiveBadge>
            <span>LIVE NOW</span>
          </LiveBadge>
        )}
        
        <TicketHeader>
          <EventType>
            {ticket.ticketType === 'premium' ? 'PREMIUM' : 'STANDARD'}
          </EventType>
          <TicketStatus 
            active={ticket.status === 'active' || ticket.status === 'completed'}
            pending={isPending}
            failed={isFailed}
          >
            {ticket.status === 'active' || ticket.status === 'completed' ? 'CONFIRMED' : 
             ticket.status === 'processing' ? 'PROCESSING' : 
             ticket.status === 'failed' ? 'FAILED' : 'PENDING'}
          </TicketStatus>
        </TicketHeader>
        
        <EventImage 
          src={ticket.event.image || 'https://via.placeholder.com/300x150?text=No+Image'} 
          alt={ticket.event.title} 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/300x150?text=Image+Error';
          }}
          past={isPast}
        />
        
        <TicketContent>
          <EventTitle>{ticket.event.title}</EventTitle>
          
          <EventDetail>
            <FaCalendar />
            <span>{new Date(ticket.event.date).toLocaleDateString()}</span>
          </EventDetail>
          
          <EventDetail>
            <FaClock />
            <span>{ticket.event.time}</span>
          </EventDetail>
          
          <EventDetail>
            <FaTicketAlt />
            <span>Ticket #{ticket.id ? ticket.id.substring(0, 8) : 'Pending'}</span>
          </EventDetail>
          
          {isPending && (
            <StatusMessage pending>
              <BsInfoCircle /> {ticket.statusMessage || 'Payment processing...'}
            </StatusMessage>
          )}
          
          {isFailed && (
            <StatusMessage failed>
              <BsInfoCircle /> Payment failed. Please try again.
            </StatusMessage>
          )}
          
          <TicketActions>
            <Button
              variant="contained"
              color={isLive ? "error" : isPast ? "secondary" : "primary"}
              onClick={() => handleAccessEvent(ticket)}
              fullWidth
              startIcon={isLive ? <FaPlay /> : isPast ? <FaPlay /> : <FaLink />}
              sx={{ mb: 1 }}
              disabled={isPending && !isPast}
            >
              {isLive ? 'Join Live Now' : 
               isPast ? (ticket.event.recordingUrl ? 'Watch Recording' : 'View Details') : 
               isPending ? 'Awaiting Payment' : 'View Details'}
            </Button>
            
            {isPending && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate(`/streaming/${ticket.event.id}`)}
                fullWidth
                size="small"
              >
                Complete Payment
              </Button>
            )}
            
            {isFailed && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => navigate(`/streaming/${ticket.event.id}`)}
                fullWidth
                size="small"
              >
                Retry Payment
              </Button>
            )}
          </TicketActions>
        </TicketContent>
      </TicketCard>
    );
  }
};

export default MyTickets;
